import { JobForInvoicing } from '../../../domain/invoicing/invoice'
import { workAreaStatusOptions } from '../workAreaStatusModel'

export type FilterValue = {
  name: string
  value: number | string
  selected: boolean
}

export type FilterCategory = {
  key: string
  label: string
  all: boolean
  values: FilterValue[]
}

export type DateRange = {
  starting: string | null
  ending: string | null
}

export type FilterSet = {
  projectType: FilterCategory
  scheduledStatus: FilterCategory
  invoicedStatus: FilterCategory
  divisions: FilterCategory
}

export type FiltersState = FilterSet & {
  visitReviewStatus?: FilterCategory
  dateRange: DateRange
  search: string
}

export type FilterStorageKeys = {
  projectType: string
  scheduledStatus: string
  invoicedStatus: string
  divisions: string
  dateRange: string
  search?: string
  visitReviewStatus?: string
}

export const STORAGE_KEYS: FilterStorageKeys = {
  projectType: 'invoicingFilter_projectType',
  scheduledStatus: 'invoicingFilter_scheduledStatus',
  invoicedStatus: 'invoicingFilter_invoicedStatus',
  divisions: 'invoicingFilter_divisions',
  dateRange: 'billing_dateRangeFilter',
} as const

export const READY_TO_INVOICE_STORAGE_KEYS: FilterStorageKeys = {
  projectType: 'readyToInvoiceFilter_projectType',
  scheduledStatus: 'readyToInvoiceFilter_scheduledStatus',
  invoicedStatus: 'readyToInvoiceFilter_invoicedStatus',
  divisions: 'readyToInvoiceFilter_divisions',
  dateRange: 'readyToInvoiceFilter_dateRange',
  search: 'readyToInvoiceFilter_search',
  visitReviewStatus: 'readyToInvoice_visitReviewStatus',
}

export const projectTypeDefaults: FilterValue[] = [
  { name: 'Project', value: 1, selected: true },
  { name: 'Service Tickets', value: 2, selected: true },
  { name: 'Maintenance', value: 3, selected: true },
]

export const scheduledStatusDefaults: FilterValue[] = workAreaStatusOptions.map(x => ({ ...x, selected: true }))

export const invoicedStatusDefaults: FilterValue[] = [
  { name: 'Not-Reviewed', value: 0, selected: true },
  { name: 'Reviewed', value: 1, selected: true },
  { name: 'Completed Visits', value: 7, selected: true },
  { name: 'Not Invoiced', value: 5, selected: true },
  { name: 'Outstanding', value: 6, selected: true },
  { name: 'Paid', value: 4, selected: true },
]

export const emptyDivisionsFilter: FilterCategory = { key: 'divisions', label: 'Divisions', all: true, values: [] }

export function isValueSelected(saved: string | null, value: number | string): boolean {
  if (saved == null) return true
  return saved.split(',').map(s => s.trim()).includes(String(value))
}

export function buildStaticFilter(key: string, label: string, defaults: FilterValue[], saved: string | null): FilterCategory {
  const values = defaults.map(d => ({
    ...d,
    selected: isValueSelected(saved, d.value),
  }))
  return {
    key,
    label,
    all: values.every(v => v.selected),
    values,
  }
}

export function buildDivisionsFilter(groupNames: string[], saved: string | null): FilterCategory {
  const seen = new Set<string>()
  const values: FilterValue[] = []

  for (const groupName of groupNames) {
    if (!seen.has(groupName)) {
      seen.add(groupName)
      values.push({
        name: groupName,
        value: groupName,
        selected: isValueSelected(saved, groupName),
      })
    }
  }

  return {
    key: 'divisions',
    label: 'Divisions',
    all: values.every(v => v.selected),
    values,
  }
}

export function updateCategory(category: FilterCategory, selectedValues: (number | string)[]): FilterCategory {
  const values = category.values.map(v => ({
    ...v,
    selected: selectedValues.includes(v.value),
  }))
  return { ...category, all: values.every(v => v.selected), values }
}

export function serializeFilter(category: FilterCategory): string {
  return category.values.filter(v => v.selected).map(v => v.value).join(',')
}

export function parseDateRange(json: string | null): DateRange {
  try {
    if (json) return JSON.parse(json)
  } catch { /* ignore parse errors */ }
  return { starting: null, ending: null }
}

export function serializeDateRange(dateRange: DateRange): string {
  return JSON.stringify(dateRange)
}

function getSelectedValues(category: FilterCategory): (number | string)[] {
  return category.values.filter(v => v.selected).map(v => v.value)
}

export type FilterJobsOptions = {
  skipDivisions?: boolean
}

export function filterJobs(jobs: JobForInvoicing[], filters?: FiltersState, options?: FilterJobsOptions): JobForInvoicing[] {
  return jobs.filter(job => {
    if (filters) {
      if (!filters.projectType.all) {
        const values = getSelectedValues(filters.projectType)
        if (!values.includes(job.type)) return false
      }

      if (!filters.scheduledStatus.all) {
        const values = getSelectedValues(filters.scheduledStatus)
        if (!job.taskStatuses?.some(ts => values.some(v => v == ts))) {
          return false
        }
      }

      if (!filters.divisions.all && !options?.skipDivisions) {
        const values = getSelectedValues(filters.divisions)
        if (!job.divisionGroups.some(d => values.includes(d))) {
          return false
        }
      }

      if (!filters.invoicedStatus.all) {
        const values = getSelectedValues(filters.invoicedStatus)
        if (!(
          values.includes(0) && job.isNotReviewed ||
          values.includes(1) && job.isReviewed ||
          values.includes(2) && job.isUninvoiced ||
          values.includes(3) && job.isInvoiced ||
          values.includes(4) && job.isPaid ||
          values.includes(5) && job.isNotInvoiced ||
          values.includes(6) && job.isOutstanding ||
          values.includes(7) && job.hasCompletedVisits
        )) return false
      }

      if (filters.search?.trim()) {
        const term = filters.search.trim().toLowerCase()
        if (!job.name.toLowerCase().includes(term) && !job.customer.displayName.toLowerCase().includes(term)) return false
      }
    }

    return true
  })
}

export const visitReviewStatusDefaults: FilterValue[] = [
  { name: 'Not Reviewed', value: 1, selected: true },
  { name: 'Reviewed', value: 2, selected: true },
]
